import React, { useEffect, useState, useCallback } from 'react';
import { listPendingInvites, resendInvite, cancelInvite } from '../services/invitationsApi';
import { useAuth } from '../../../context/AuthContext';
import '../styles/dialog.css';
import '../styles/pending-invites.css';

const IconRefresh = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <path d="M23 4v6h-6" />
    <path d="M1 20v-6h6" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

const IconSend = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

const IconCancel = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <circle cx="12" cy="12" r="10" />
    <line x1="15" y1="9" x2="9" y2="15" />
    <line x1="9" y1="9" x2="15" y2="15" />
  </svg>
);

/**
 * @param {{ companyData: object, isActive?: boolean }} props
 * When embedded in PendingInvitesDialog, pass isActive=true when open to refetch.
 */
export default function PendingInvitesList({ companyData, isActive = true }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await listPendingInvites({
        companyId: companyData?.id,
        ownerId: companyData?.ownerId,
        currentUser: user,
      });
      setItems(data);
    } catch (e) {
      setError(e?.message || 'Failed to load invites');
    } finally {
      setLoading(false);
    }
  }, [companyData?.id, companyData?.ownerId, user]);

  useEffect(() => {
    if (companyData?.id && isActive) load();
  }, [companyData?.id, isActive, load]);

  const handleResend = async (token) => {
    await resendInvite({ companyId: companyData?.id, token, currentUser: user });
    await load();
  };

  const handleCancel = async (token) => {
    await cancelInvite({ companyId: companyData?.id, token, currentUser: user });
    await load();
  };

  return (
    <div>
      <div className="pending-invites-toolbar">
        <button
          type="button"
          className="pending-invites-refresh"
          onClick={load}
          disabled={loading}
          title="Refresh list"
          aria-label="Refresh pending invites"
        >
          <IconRefresh />
        </button>
      </div>

      {error ? <div className="pending-invites-error">{error}</div> : null}

      {loading && items.length === 0 ? (
        <div className="pending-invites-loading" aria-busy="true">
          <span className="spinner" />
        </div>
      ) : null}

      {!loading && items.length === 0 ? (
        <p className="pending-invites-empty">No pending invites.</p>
      ) : null}

      {items.length > 0 ? (
        <div className="pending-invites-list">
          {items.map((inv) => (
            <div key={inv.token} className="pending-invites-row">
              <span className="pending-invites-email">{inv.email}</span>
              <span className="pending-invites-role-chip">{inv.role}</span>
              <div className="pending-invites-actions">
                <button
                  type="button"
                  className="pending-invites-icon-btn"
                  title="Resend invite"
                  aria-label={`Resend invite to ${inv.email}`}
                  onClick={() => handleResend(inv.token)}
                >
                  <IconSend />
                </button>
                <button
                  type="button"
                  className="pending-invites-icon-btn pending-invites-icon-btn--danger"
                  title="Cancel invite"
                  aria-label={`Cancel invite for ${inv.email}`}
                  onClick={() => handleCancel(inv.token)}
                >
                  <IconCancel />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
